import { Component, Input, OnInit, OnChanges, SimpleChanges, NO_ERRORS_SCHEMA, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgRadialGaugeOptions } from 'ag-charts-enterprise';
import { AgChartsModule } from 'ag-charts-angular'; 
// At the top of your file, add this interface
interface ExtendedRadialGaugeOptions extends AgRadialGaugeOptions {
  centerLabel?: {
    fontSize?: number;
    [key: string]: any;
  };
  centerValue?: {
    fontSize?: number;
    positionY?: number;
    [key: string]: any;
  };
}

@Component({
  selector: 'app-radial-gauge-chart',
  templateUrl: './radial-gauge-chart.component.html',
  styleUrls: ['./radial-gauge-chart.component.scss'],
  standalone: true,
  imports: [CommonModule, AgChartsModule],
  schemas: [NO_ERRORS_SCHEMA]
})
export class RadialGaugeChartComponent implements OnInit, OnChanges {
  @Input() title: string = '';
  @Input() value: number = 0;
  @Input() currentYearTarget: number = 0;
  @Input() monthTarget: number = 0;
  @Input() maxValue: number = 100;
  @Input() color: string = '#4169E1'; // Royal blue as default
  @Input() targetColor: string = '#ff9900'; // Lighter blue for target
  @Input() label: string = '';
  @Input() change: number = 0;
  @Input() showValueOnce: boolean = false;
  @Input() showMonthTarget: boolean = false;
  @Input() showChange: boolean = true;
  @Input() alwaysShowSuffix: string = '';
  @Input() changeValue: number = 0;
  @Input() showChangeIndicator: boolean = true;
  @Input() lastYearActual?: number; // Make the property optional with ?
  @Input() growthPercentage: number = 0;
  @Input() showGrowthPercentage: boolean = false;
  @Input() useAbbreviatedValues: boolean = false;
  @Input() compactMode: boolean = false; // New property for extra compact display
  @Input() percentChangeTargetYearOverYear: number = 0;
  @Input() percentChangeActualVsTarget: number = 0;
  @Input() showPercentChange: boolean = true;

  currentYear: number = new Date().getFullYear();
  // Add Math property to make it available in the template
  protected Math = Math;
  
  // Track screen size
  screenWidth: number = window.innerWidth;
  screenHeight: number = window.innerHeight;
  
  // Detect specific laptop resolutions
  isSmallLaptop: boolean = false;
  isMobile: boolean = false;
  isTablet: boolean = false;
  isDesktop: boolean = false;
  
  // Automatically detect compact mode based on screen size
  autoCompactMode: boolean = false;

  // Change the type of options
  public options: ExtendedRadialGaugeOptions;

  constructor() {
    this.options = this.getDefaultOptions();
    this.updateScreenSizeFlags();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.screenWidth = window.innerWidth;
    this.screenHeight = window.innerHeight;
    this.updateScreenSizeFlags();
    this.updateChart();
  }
  
  private updateScreenSizeFlags() {
    // Detect specific laptop resolutions
    this.isSmallLaptop = 
      (this.screenWidth >= 1200 && this.screenWidth <= 1440 && this.screenHeight <= 900) ||
      (this.screenWidth >= 1280 && this.screenWidth <= 1366 && this.screenHeight <= 800);
      
    this.isMobile = this.screenWidth < 576;
    this.isTablet = this.screenWidth >= 576 && this.screenWidth < 992;
    this.isDesktop = this.screenWidth >= 992;
    
    // Auto-enable compact mode for small screens
    this.autoCompactMode = this.isSmallLaptop || this.isMobile || 
                          (this.screenWidth < 1400 && this.screenHeight < 800);
  }

  private getDefaultOptions(): ExtendedRadialGaugeOptions {
    return {
      type: 'radial-gauge',
      value: this.value,
      scale: {
        min: 0,
        max: this.maxValue,
        label: {
          formatter: (params: any) => this.formatAxisValue(params.value)
        }
      },
      targets: [
        {
          placement: 'middle',
          shape: 'line',
          fill: '#ff6949',
          strokeWidth: 25,
          stroke: '#ff6949',
          spacing: 3,
          value: this.currentYearTarget,
          size: this.isMobile ? 2 : 3,
        }
      ],
      // Top half-circle configuration
      startAngle: -90,
      endAngle: 90,
      background: {
        fill: '#ffffff' // Light gray background for gauge
      },
      bar: {
        fill: this.color,
      },
      // Add professional styling that is supported
      title: {
        enabled: false
      },
      subtitle: {
        enabled: false
      },
      padding: {
        top: this.isMobile ? 5 : (this.isSmallLaptop ? 10 : 20),
        right: this.isMobile ? 5 : (this.isSmallLaptop ? 10 : 20),
        bottom: this.isMobile ? 5 : (this.isSmallLaptop ? 10 : 20),
        left: this.isMobile ? 5 : (this.isSmallLaptop ? 10 : 20)
      },
      
      // Responsive size
      minWidth: undefined,
      minHeight: undefined,
      
      label: {
        enabled: true,        
        fontSize: this.isMobile ? 16 : (this.isTablet ? 20 : (this.isSmallLaptop ? 18 : 24)),
        fontWeight: 700,
        color: '#333',
        formatter: (params: any) => this.formatValue(params.value)
      }
    };
  }

  ngOnInit(): void {
    this.updateScreenSizeFlags();
    this.updateChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] || changes['maxValue'] || changes['color'] || 
        changes['currentYearTarget'] || changes['targetColor'] || changes['compactMode'] ||
        changes['monthTarget']) {
      this.updateChart();
    }
  }

  private updateChart(): void {
    const useCompactMode = this.compactMode || this.autoCompactMode;
    
    this.options = {
      ...this.options,
      value: this.value,
      scale: {
        min: 0,
        max: this.maxValue,
        label: {
          formatter: (params: any) => this.formatAxisValue(params.value)
        }
      },
      bar: {
        fill: this.color || '#4169E1',
      },
      targets: [
        {
          placement: 'middle',
          shape: 'line',
          fill: '#ff6949',
          strokeWidth: 25,
          stroke: '#ff6949',
          spacing: useCompactMode ? 3 : 5,
          value: this.currentYearTarget,
          size: useCompactMode ? (this.isMobile ? 1 : 2) : (this.isMobile ? 2 : 3),
        }
      ],
      padding: {
        top: useCompactMode ? (this.isMobile ? 2 : 5) : (this.isMobile ? 5 : (this.isTablet ? 10 : (this.isSmallLaptop ? 10 : 20))),
        right: useCompactMode ? (this.isMobile ? 2 : 5) : (this.isMobile ? 5 : (this.isTablet ? 10 : (this.isSmallLaptop ? 10 : 20))),
        bottom: useCompactMode ? (this.isMobile ? 2 : 5) : (this.isMobile ? 5 : (this.isTablet ? 10 : (this.isSmallLaptop ? 10 : 20))),
        left: useCompactMode ? (this.isMobile ? 2 : 5) : (this.isMobile ? 5 : (this.isTablet ? 10 : (this.isSmallLaptop ? 10 : 20)))
      },
      label: {
        enabled: true,        
        fontSize: useCompactMode ? (this.isMobile ? 14 : 16) : (this.isMobile ? 16 : (this.isTablet ? 20 : (this.isSmallLaptop ? 18 : 24))),
        fontWeight: 700,
        color: '#333',
        formatter: (params: any) => this.formatValue(params.value)
      }
    };
    
    // Set CSS variable for target color
    document.documentElement.style.setProperty('--target-color', this.targetColor || '#3498db');

    // Adjust chart options based on screen size
    if (useCompactMode) {
      // Optimize for small laptops and compact mode
      this.options = {
        ...this.options,
        padding: {
          top: this.isMobile ? 2 : 5,
          right: this.isMobile ? 2 : 5,
          bottom: this.isMobile ? 2 : 5,
          left: this.isMobile ? 2 : 5
        },
        // Use type assertion to bypass TypeScript checking
        ...(this.options as any).centerLabel ? {
          centerLabel: {
            ...(this.options as any).centerLabel,
            fontSize: this.isMobile ? 11 : 13
          }
        } : {},
        ...(this.options as any).centerValue ? {
          centerValue: {
            ...(this.options as any).centerValue,
            fontSize: this.isMobile ? 14 : 16
          }
        } : {}
      };
    }
    
    // Ensure text remains visible by adjusting positioning
    if (useCompactMode && (this.options as any).centerValue) {
      (this.options as any).centerValue.positionY = 0.52; // Adjust vertical position
    }
  }

  private formatAxisValue(value: number): string {
    if (value >= 1000000) {
      return (value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1).replace(/\.0$/, '') + 'M';
    } else if (value >= 1000) {
      return (value / 1000).toFixed(value % 1000 === 0 ? 0 : 1).replace(/\.0$/, '') + 'k';
    }
    return value.toString();
  }

  // Get current month name (3-letter abbreviation)
  getCurrentMonthName(): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonth = new Date().getMonth();
    return months[currentMonth];
  }

  formatValue(value: number): string {
    if (this.alwaysShowSuffix) {
      return value.toFixed(1).replace(/\.0$/, '') + this.alwaysShowSuffix;
    }
    
    if (this.useAbbreviatedValues) {
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
      } else if (value >= 1000) {
        return (value / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
      }
    }
    
    return value.toLocaleString();
  }

  getChangeClass(): string {
    return this.change > 0 ? 'text-success' : this.change < 0 ? 'text-danger' : 'text-muted';
  }

  getChangeIcon(): string {
    return this.change > 0 ? 'bi-arrow-up' : this.change < 0 ? 'bi-arrow-down' : 'bi-dash';
  }

  // Add a method to get the absolute value of change
  getAbsChange(): string {
    return Math.abs(this.change).toFixed(1);
  }

  // Add this method to calculate completion percentage
  getCompletionPercentage(): string {
    if (!this.currentYearTarget || this.currentYearTarget === 0) return '0';
    const percentage = (this.value / this.currentYearTarget) * 100;
    return percentage.toFixed(1);
  }

  // Add a method to determine the completion percentage class
  getCompletionPercentageClass(): string {
    if (!this.currentYearTarget || this.currentYearTarget === 0) return '';
    const percentage = (this.value / this.currentYearTarget) * 100;
    return percentage >= 100 ? 'positive' : 'negative';
  }

  // Format numbers with commas but without abbreviations
  formatValueWithoutAbbreviation(value: number): string {
    // If we have a specific suffix that should always be shown
    if (this.alwaysShowSuffix) {
      return value.toFixed(1).replace(/\.0$/, '') + this.alwaysShowSuffix;
    }
    
    // Otherwise just use regular comma formatting
    return value.toLocaleString();
  }

  getChangeIndicatorClass(): string {
    if (this.change > 0) {
      return 'positive-change';
    } else if (this.change < 0) {
      return 'negative-change';
    }
    return 'neutral-change';
  }

  formatChangeValue(value: number): string {
    // Special case for zero
    if (value === 0) {
      return '0%';
    }
    
    // Format the change value, e.g., "+15%" or "-10%"
    const prefix = value > 0 ? '+' : '';
    return `${prefix}${value}%`;
  }
  
  // Helper method to determine if we should use compact layout
  get shouldUseCompactLayout(): boolean {
    return this.compactMode || this.autoCompactMode;
  }

  formatPercentage(value: number): string {
    return value.toFixed(1) + '%';
  }

  getValuePercentage(): number {
    return (this.value / this.maxValue) * 100;
  }

  getTargetPercentage(): number {
    return (this.currentYearTarget / this.maxValue) * 100;
  }

  getMonthTargetPercentage(): number {
    return (this.monthTarget / this.maxValue) * 100;
  }
}